import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { ROOT_URL } from '../../api/ApiConfig';
import { appendGroupIdParam, applyAnalyticsPendingLoading, markAnalyticsFetchKey } from '../../utils/analyticsQuery';

export const getChatPerformanceStats = createAsyncThunk(
  'chatPerformanceStats/getChatPerformanceStats',
  async ({ range, startDate, endDate, group_id }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { loginToken } = getState();
      const authToken = loginToken?.response?.token;
      const businessId = state.loginToken?.response?.user?.business_id;
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      if (!businessId) {
        throw new Error('Business ID not found');
      }

      let url = `${ROOT_URL}/analytics/chat-performance/${businessId}`;
      let params = new URLSearchParams();
      
      params.append('timezone', timezone);
      
      if (range === 'custom' && startDate && endDate) {
        params.append('range', 'custom');
        params.append('from', startDate);
        params.append('to', endDate);
      } else {
        const apiRange = range === '365d' ? '1y' : range;
        params.append('range', apiRange);
      }

      appendGroupIdParam(params, group_id);
      
      url += `?${params.toString()}`;
      
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching chat performance stats:', error);
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch chat performance stats'
      );
    }
  }
);

const initialState = {
  loading: false,
  error: null,
  data: null,
  lastFetchKey: null,
  chartData: [],
  range: null,
  from: null,
  to: null
};

const chatPerformanceStatsSlice = createSlice({
  name: 'chatPerformanceStats',
  initialState,
  reducers: {
    resetChatPerformanceStats: (state) => {
      return initialState;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getChatPerformanceStats.pending, (state, action) => {
        applyAnalyticsPendingLoading(state, action.meta?.arg);
        state.error = null;
      })
      .addCase(getChatPerformanceStats.fulfilled, (state, action) => {
        state.loading = false;
        markAnalyticsFetchKey(state, action.meta?.arg);
        state.error = null;
        state.data = action.payload.data;
        state.chartData = action.payload.data.data || [];
        state.range = action.payload.data.range;
        state.from = action.payload.data.from;
        state.to = action.payload.data.to;
      })
      .addCase(getChatPerformanceStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.data = null;
        state.chartData = [];
      });
  }
});

export const { resetChatPerformanceStats, clearError } = chatPerformanceStatsSlice.actions;

export default chatPerformanceStatsSlice.reducer;
